/**
 * Offline license verification (Ed25519, Web Crypto). No server, no network.
 *
 * A license token is issued by the MOBILE app while the user's Pro subscription
 * is active, signed with a PRIVATE key that lives only in the phone app. The web
 * app bundles only the matching PUBLIC key and verifies signatures locally.
 *
 * This is a deterrent, not DRM — a determined user can patch client JS. The goal
 * is to keep the Pro-level web features tied to an active store subscription for
 * honest users, with zero backend.
 *
 * Token format:  CSL1.<base64url(payloadJSON)>.<base64url(signature)>
 *   payload = { v:1, tier:"pro", iat:<epochSec>, exp?:<epochSec>, sub?:<string> }
 *   signature = Ed25519 over the UTF-8 bytes of the payload JSON.
 */

export interface LicensePayload {
  v: number;
  tier: "pro";
  iat: number;
  exp?: number;
  sub?: string; // browser pairing id — binds the code to one browser
  sid?: string; // stable subscriber/purchase id — groups all codes from one purchase
}

const PREFIX = "CSL1";
const enc = new TextEncoder();
const dec = new TextDecoder();

function b64urlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function b64urlDecode(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function importPublicKey(rawB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", b64urlDecode(rawB64), { name: "Ed25519" }, false, ["verify"]);
}

export interface VerifyResult {
  ok: boolean;
  // "device" = signature/claims valid, but the code was issued for a different
  // browser (its `sub` doesn't match this browser's pairing id).
  reason?: "format" | "signature" | "expired" | "tier" | "device";
  payload?: LicensePayload;
}

/**
 * Verify a token against a raw base64url Ed25519 public key. Checks the signature
 * and (if present) expiry. Returns ok:false with a reason on any failure.
 */
export async function verifyLicense(token: string, publicKeyB64: string, now = Math.floor(Date.now() / 1000)): Promise<VerifyResult> {
  const parts = token.trim().split(".");
  if (parts.length !== 3 || parts[0] !== PREFIX) return { ok: false, reason: "format" };
  let payload: LicensePayload;
  let payloadBytes: Uint8Array<ArrayBuffer>;
  let sig: Uint8Array<ArrayBuffer>;
  try {
    payloadBytes = b64urlDecode(parts[1]!);
    sig = b64urlDecode(parts[2]!);
    payload = JSON.parse(dec.decode(payloadBytes));
  } catch {
    return { ok: false, reason: "format" };
  }
  let verified = false;
  try {
    const key = await importPublicKey(publicKeyB64);
    verified = await crypto.subtle.verify({ name: "Ed25519" }, key, sig, payloadBytes);
  } catch {
    return { ok: false, reason: "signature" };
  }
  if (!verified) return { ok: false, reason: "signature" };
  if (payload.tier !== "pro") return { ok: false, reason: "tier", payload };
  if (payload.exp != null && now > payload.exp) return { ok: false, reason: "expired", payload };
  return { ok: true, payload };
}

// ---- Tooling / phone-side reference (also used by tests) --------------------

/** Sign a payload with an Ed25519 private CryptoKey → token string. */
export async function signLicense(payload: LicensePayload, privateKey: CryptoKey): Promise<string> {
  const payloadBytes = enc.encode(JSON.stringify(payload));
  const sig = new Uint8Array(await crypto.subtle.sign({ name: "Ed25519" }, privateKey, payloadBytes));
  return `${PREFIX}.${b64urlEncode(payloadBytes)}.${b64urlEncode(sig)}`;
}

/** Generate a fresh Ed25519 keypair; returns raw public (b64url) + pkcs8 private (b64url). */
export async function generateKeypair(): Promise<{ publicKeyB64: string; privateKeyPkcs8B64: string; keys: CryptoKeyPair }> {
  const keys = (await crypto.subtle.generateKey({ name: "Ed25519" }, true, ["sign", "verify"])) as CryptoKeyPair;
  const rawPub = new Uint8Array(await crypto.subtle.exportKey("raw", keys.publicKey));
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey("pkcs8", keys.privateKey));
  return { publicKeyB64: b64urlEncode(rawPub), privateKeyPkcs8B64: b64urlEncode(pkcs8), keys };
}

export async function importPrivateKeyPkcs8(b64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", b64urlDecode(b64), { name: "Ed25519" }, true, ["sign"]);
}
